import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, HelpCircle, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const UserProfileDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { name: userName, role: userRole, logout } = useAuth();

    // Get user details
    const displayName = userName || 'Student';
    const displayRole = userRole || 'client';
    const initials = displayName.charAt(0).toUpperCase();

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    const dashboardLink = displayRole === 'admin' ? '/admin/dashboard' : '/student/dashboard';

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 text-white hover:text-[#009B8F] transition-colors group"
            >
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold border border-transparent group-hover:border-[#009B8F] transition-all">
                    {initials}
                </div>
                <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 mt-3 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 animate-fadeIn">
                    <div className="px-4 py-3 border-b border-gray-50">
                        <p className="text-sm font-bold text-[#011E26] truncate">{displayName}</p>
                        <p className="text-xs text-slate-500 capitalize">{displayRole}</p>
                    </div>

                    <div className="py-1">
                        <Link
                            to={dashboardLink}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-gray-50 hover:text-[#009B8F] transition-colors"
                        >
                            <LayoutDashboard size={16} />
                            Dashboard
                        </Link>
                        <Link
                            to="/student/profile"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-gray-50 hover:text-[#009B8F] transition-colors"
                        >
                            <User size={16} />
                            My Profile
                        </Link>
                        <Link
                            to="/settings"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-gray-50 hover:text-[#009B8F] transition-colors"
                        >
                            <Settings size={16} />
                            Account Settings
                        </Link>
                        <Link
                            to="/contact"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-gray-50 hover:text-[#009B8F] transition-colors"
                        >
                            <HelpCircle size={16} />
                            Help & Support
                        </Link>
                    </div>

                    <div className="border-t border-gray-50 mt-1 pt-1">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                        >
                            <LogOut size={16} />
                            Logout
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfileDropdown;
